// useJarvisVoice: Jarvis-style Intelligent AI Assistant
//  - Auto-detects speech start/stop with silence detection
//  - Real-time captions for user and AI
//  - Detailed self-introduction
//  - Intelligent conversation flow
//  - Visual feedback like Jarvis
import { useCallback, useEffect, useRef, useState } from "react";
import { chat, API } from "./api";

// N.E.T.R.A's warm, engaging introduction
const NETRA_INTRODUCTION = {
  en: `System online. I am N.E.T.R.A, your forensic truth companion. Ask me anything, or paste a link, and I will extract the reality behind the narrative.`,
  hi: `नमस्ते! मैं नेत्रा हूँ, आपकी सच्चाई की साथी। कोई भी खबर, लिंक या दावा मुझे बताइए, और मैं आपको बताऊंगी कि असल में क्या हो रहा है।`,
};

// Short, warm follow-up greetings
const WELCOME_GREETINGS = {
  en: [
    "System online. N.E.T.R.A here. Let's find the truth.",
    "N.E.T.R.A at your service. What are we investigating today?",
    "Voice module active. I'm N.E.T.R.A. Ready to analyze.",
    "Hello. I'm N.E.T.R.A, your forensic analyst. What's the target?",
    "N.E.T.R.A listening. Share the claim, and I'll break it down.",
    "Awaiting input. What should we verify?",
  ],
  hi: [
    "आज क्या जानना चाहेंगे?",
    "जब चाहें, मुझसे पूछें।",
  ],
};



export const LANGUAGES = [
  { code: "en", label: "English", bcp47: "en-US" },
  { code: "hi", label: "हिंदी", bcp47: "hi-IN" },
];

export const isSpeechSupported = () =>
  typeof window !== "undefined" &&
  (("SpeechRecognition" in window) || ("webkitSpeechRecognition" in window));

export const isTtsSupported = () =>
  typeof window !== "undefined" && "speechSynthesis" in window;

// Pick best voice — Jenny Online Natural first, then any Natural/Online, then Jenny offline
function pickVoice(voices, bcp47, preferredName) {
  if (!voices?.length) return null;

  if (preferredName) {
    const pref = voices.find(v => v.name === preferredName);
    if (pref) return pref;
  }

  const lang = bcp47.toLowerCase();
  const langPrefix = lang.split("-")[0];
  const exact   = voices.filter((v) => v.lang.toLowerCase() === lang);
  const partial = voices.filter((v) => v.lang.toLowerCase().startsWith(langPrefix));
  const pool    = exact.length ? exact : partial.length ? partial : voices;

  // Tier 1 — Ultimate Defaults based on language
  if (langPrefix === "hi") {
    const swaraOnline = pool.find(v => v.name.toLowerCase().includes("swara") && (v.name.toLowerCase().includes("online") || v.name.toLowerCase().includes("natural")));
    if (swaraOnline) return swaraOnline;
    const swaraFallback = pool.find(v => v.name.toLowerCase().includes("swara"));
    if (swaraFallback) return swaraFallback;
    const kalpana = pool.find(v => v.name.toLowerCase().includes("kalpana"));
    if (kalpana) return kalpana;
  } else {
    // English (or other) default
    const jennyNatural = pool.find((v) =>
      v.name.toLowerCase().includes("jenny") &&
      (v.name.toLowerCase().includes("natural") || v.name.toLowerCase().includes("online"))
    );
    if (jennyNatural) return jennyNatural;
  }

  // Tier 2 — Aria or any other Microsoft Online (Natural) female voice
  const anyNatural = pool.find((v) =>
    !v.name.toLowerCase().includes("aarav") &&
    !v.name.toLowerCase().includes("arav") &&
    !v.name.toLowerCase().includes("david") &&
    !v.name.toLowerCase().includes("mark") &&
    !v.name.toLowerCase().includes("ravi") &&
    (v.name.toLowerCase().includes("natural") ||
    (v.name.toLowerCase().includes("online") && v.name.toLowerCase().includes("microsoft")))
  );
  if (anyNatural) return anyNatural;

  // Tier 3 — Google US/UK English Female or Samantha (macOS)
  const premiumOffline = pool.find((v) => 
    v.name.toLowerCase().includes("google us english") ||
    v.name.toLowerCase().includes("google uk english female") ||
    v.name.toLowerCase() === "samantha"
  );
  if (premiumOffline) return premiumOffline;

  // Tier 4 — Neural voices
  const neural = pool.find((v) => v.name.toLowerCase().includes("neural"));
  if (neural) return neural;

  // Tier 5 — Jenny offline fallback
  const jenny = pool.find((v) => v.name.toLowerCase().includes("jenny"));
  if (jenny) return jenny;

  // Tier 6 — Other named female voices (Zira is a last resort)
  const preferred = [
    "Microsoft Aria", "Microsoft Zira",
    "Google UK English Female", "Samantha",
    "Swara", "Kalpana", "Google हिन्दी", "Veena", "Aditi", "Female",
  ];
  for (const name of preferred) {
    const m = pool.find((v) => v.name.toLowerCase().includes(name.toLowerCase()));
    if (m) return m;
  }
  return pool[0] || null;
}

// Audio cue for Jarvis feel
function playJarvisCue(type) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    const now = ctx.currentTime;
    if (type === "start") {
      // Jarvis activation sound - rising tone
      osc.type = "sine";
      osc.frequency.setValueAtTime(440, now);
      osc.frequency.exponentialRampToValueAtTime(880, now + 0.15);
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.1, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
      osc.start(now);
      osc.stop(now + 0.25);
    } else if (type === "stop") {
      // Confirmation tone
      osc.type = "sine";
      osc.frequency.setValueAtTime(660, now);
      osc.frequency.exponentialRampToValueAtTime(440, now + 0.1);
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.08, now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
      osc.start(now);
      osc.stop(now + 0.2);
    } else if (type === "thinking") {
      // Processing sound
      osc.type = "triangle";
      osc.frequency.setValueAtTime(523, now);
      gain.gain.setValueAtTime(0.03, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
      osc.start(now);
      osc.stop(now + 0.12);
    }
    setTimeout(() => { try { ctx.close(); } catch {} }, 400);
  } catch {}
}

export default function useJarvisVoice({ 
  onError, 
  autoStart = false
} = {}) {
  const [supported] = useState(() => isSpeechSupported() && isTtsSupported());
  const [phase, setPhase] = useState("idle"); // idle, listening, thinking, speaking, introducing
  const [language, setLanguage] = useState("en");
  const [voices, setVoices] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [selectedVoiceName, setSelectedVoiceName] = useState(""); // which TTS voice is active
  const [preferredVoiceName, setPreferredVoiceName] = useState(""); // user's manual choice
  
  // Real-time captions
  const [userCaption, setUserCaption] = useState("");
  const [aiCaption, setAiCaption] = useState("");
  const [isInterim, setIsInterim] = useState(false);
  
  // Visual feedback
  const [audioLevel, setAudioLevel] = useState(0);
  const [isIntroduced, setIsIntroduced] = useState(false);
  const [modelUsed, setModelUsed] = useState(""); // tracks which AI backend responded
  
  const recognitionRef       = useRef(null);
  const phaseRef             = useRef("idle");
  const finalTranscriptRef   = useRef("");
  const audioCtxRef          = useRef(null);
  const analyserRef          = useRef(null);
  const mediaStreamRef       = useRef(null);
  const meterRafRef          = useRef(null);
  const silenceTimeoutRef    = useRef(null);
  const hasIntroducedRef     = useRef(false);
  const networkRetryRef      = useRef(0);   // STT network retry counter
  const listeningActiveRef   = useRef(false); // guard against double-start
  const prevLangRef          = useRef(language);

  // Announce language switches interactively
  useEffect(() => {
    if (prevLangRef.current !== language && voices.length > 0) {
      if (isIntroduced || hasIntroducedRef.current) {
        try { window.speechSynthesis.cancel(); } catch {}
        const bcp47 = language === "hi" ? "hi-IN" : "en-US";
        const msg = language === "hi" 
          ? "अब आप नेत्रा से हिंदी में बात कर सकते हैं।" 
          : "Language switched. Netra is now listening in English.";
        const u = new SpeechSynthesisUtterance(msg);
        u.lang = bcp47;
        u.rate = 0.96;
        u.pitch = 1.0;
        const v = pickVoice(voices, bcp47, preferredVoiceName);
        if (v) u.voice = v;
        
        window.speechSynthesis.speak(u);
      }
      prevLangRef.current = language;
    }
  }, [language, voices, isIntroduced, preferredVoiceName]);

  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // Load TTS voices
  useEffect(() => {
    if (!isTtsSupported()) return;
    const load = () => setVoices(window.speechSynthesis.getVoices() || []);
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  // Initialize speech recognition - use non-continuous for stability
  useEffect(() => {
    if (!isSpeechSupported()) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.continuous = false;  // Single-shot mode to avoid network errors
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    // Increase timeout for better recognition
    // @ts-ignore - some browsers support this
    if (rec.maxSpeechTime !== undefined) rec.maxSpeechTime = 30000; // 30 seconds max
    recognitionRef.current = rec;
    return () => { try { rec.abort(); } catch {} };
  }, []);

  // Audio meter for visual feedback
  const startAudioMeter = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512; // Increased for better frequency resolution
      source.connect(analyser);
      analyserRef.current = analyser;

      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        setAudioLevel(Math.min(1, rms * 2.5));
        meterRafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch {
      // Mic permission denied - still allow STT
    }
  }, []);

  const stopAudioMeter = useCallback(() => {
    if (meterRafRef.current) cancelAnimationFrame(meterRafRef.current);
    meterRafRef.current = null;
    setAudioLevel(0);
    try { analyserRef.current?.disconnect(); } catch {}
    analyserRef.current = null;
    try { audioCtxRef.current?.close(); } catch {}
    audioCtxRef.current = null;
    try { mediaStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    mediaStreamRef.current = null;
  }, []);

  // TTS with caption sync using Microsoft Edge TTS (Backend-streamed MP3)
  const speakWithCaption = useCallback(async (text, langCode, captionSetter) => {
    if (!text) {
      captionSetter(text);
      return Promise.resolve();
    }
    
    return new Promise(async (resolve) => {
      // Stop any existing audio
      try { 
        if (window.__currentAudio) {
          window.__currentAudio.pause();
          window.__currentAudio.currentTime = 0;
        }
      } catch {}
      try { window.speechSynthesis.cancel(); } catch {}
      
      const cleanText = text.replace(/N\.E\.T\.R\.A\.?/gi, "Netra").replace(/नेत्रा/g, "नेत्रा");
      captionSetter(text);
      setPhase("speaking");
      setSelectedVoiceName(langCode === "hi" ? "Microsoft Swara (Hindi)" : "Microsoft Jenny (English)");
      
      try {
        console.log("[N.E.T.R.A Voice] Requesting Edge TTS from backend...");
        const response = await fetch(`${API}/chat/tts`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: cleanText, language: langCode }),
        });
        
        if (!response.ok) throw new Error("TTS endpoint returned " + response.status);
        
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        window.__currentAudio = audio;
        
        audio.onended = () => {
          setPhase("idle");
          URL.revokeObjectURL(url);
          resolve();
        };
        audio.onerror = (e) => {
          console.error("[N.E.T.R.A Voice] Audio playback error:", e);
          setPhase("idle");
          URL.revokeObjectURL(url);
          resolve();
        };
        
        await audio.play();
      } catch (err) {
        console.error("[N.E.T.R.A Voice] Backend TTS error:", err);
        setPhase("idle");
        resolve();
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Send message to AI with intelligent routing
  const sendMessage = useCallback(async (message, langCode) => {
    if (!message.trim()) return;
    
    setPhase("thinking");
    playJarvisCue("thinking");
    setUserCaption(message);
    setAiCaption(""); // Clear previous response immediately
    setIsInterim(false);
    
    try {
      const sid = sessionId || crypto.randomUUID();
      if (!sessionId) setSessionId(sid);
      
      // Send to AI (backend intelligently routes to available AI)
      const result = await chat(message, langCode, sid, false);
      
      // Track which backend responded
      if (result.model_used) setModelUsed(result.model_used);
      
      const response = result.response || "I'm here, just give me a moment to think.";
      setAiCaption(response);
      
      // Speak the response
      await speakWithCaption(response, langCode, setAiCaption);
      
    } catch (error) {
      console.error("AI chat error:", error);
      const errorMsg = "Sorry, something got in the way. Mind trying that again?";
      setAiCaption(errorMsg);
      await speakWithCaption(errorMsg, langCode, setAiCaption);
      if (onError) onError(errorMsg);
    }
  }, [sessionId, speakWithCaption, onError]);

  // Detailed Jarvis-style introduction
  const introduce = useCallback(async () => {
    if (hasIntroducedRef.current) return;
    hasIntroducedRef.current = true;
    setIsIntroduced(true);
    
    const intro = NETRA_INTRODUCTION[language] || NETRA_INTRODUCTION.en;
    const greetings = WELCOME_GREETINGS[language] || WELCOME_GREETINGS.en;
    const greeting = greetings[Math.floor(Math.random() * greetings.length)];
    
    setPhase("introducing");
    
    // Speak full introduction
    await speakWithCaption(intro, language, setAiCaption);
    
    // Short pause
    await new Promise(r => setTimeout(r, 500));
    
    // Welcome greeting
    await speakWithCaption(greeting, language, setAiCaption);
    
    setPhase("idle");
  }, [language, speakWithCaption]);

  // Auto-introduction on mount
  useEffect(() => {
    if (supported && voices.length > 0 && !hasIntroducedRef.current && autoStart) {
      const timer = setTimeout(() => {
        introduce();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [supported, voices, autoStart, introduce]);

  // Start listening with auto-stop on silence
  const startListening = useCallback(() => {
    if (!supported) {
      onError?.("Voice not supported in this browser");
      return;
    }

    // UNLOCK HACK: Forcefully unlock Chrome's TTS engine during the user's click gesture
    // This legally bypasses Autoplay policies so the voice won't be blocked if the AI API takes 5 seconds to respond!
    try {
      window.speechSynthesis.resume();
      const unlock = new SpeechSynthesisUtterance(' ');
      unlock.volume = 0;
      window.speechSynthesis.speak(unlock);
    } catch(e) {}

    // If already listening, stop
    if (phaseRef.current === "listening") {
      try { recognitionRef.current?.stop(); } catch {}
      return;
    }

    // If speaking, stop first
    if (phaseRef.current === "speaking" || phaseRef.current === "introducing") {
      try { window.speechSynthesis.cancel(); } catch {}
      setPhase("idle");
      setTimeout(() => doStart(), 100);
      return;
    }

    doStart(); // manual start, resets retry counter

    function doStart(isAutoRetry = false) {
      if (!isAutoRetry) networkRetryRef.current = 0;
      if (listeningActiveRef.current) return; // prevent double-start
      const rec = recognitionRef.current;
      if (!rec) return;

      const meta = LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];
      rec.lang = meta.bcp47;

      finalTranscriptRef.current = "";
      setUserCaption("");
      setIsInterim(true);
      listeningActiveRef.current = true;

      rec.onresult = (e) => {
        let interim = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const t = e.results[i][0].transcript;
          if (e.results[i].isFinal) {
            finalTranscriptRef.current += t;
          } else {
            interim += t;
          }
        }
        const currentCaption = finalTranscriptRef.current || interim;
        setUserCaption(currentCaption);

        // Reset silence timer whenever new speech comes in
        if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = setTimeout(() => {
          // User stopped talking — commit
          if (phaseRef.current === "listening") {
            try { rec.stop(); } catch {}
          }
        }, 2000); // 2s silence = done speaking
      };

      rec.onstart = () => {
        console.log("[Jarvis STT] Listening started (", rec.lang, ")");
        playJarvisCue("start");
        setPhase("listening");
        startAudioMeter();
      };

      rec.onerror = (e) => {
        console.log("[Jarvis STT] Error:", e.error);
        listeningActiveRef.current = false;
        stopAudioMeter();

        if (e.error === "no-speech") {
          // User was silent — show idle, no error message needed
          setPhase("idle");
          setIsInterim(false);
          return;
        }

        if (e.error === "aborted") {
          // Manual abort (click-to-stop), not an error
          return;
        }

        if (e.error === "network") {
          // Auto-retry up to 3 times before showing the error
          if (networkRetryRef.current < 3) {
            networkRetryRef.current++;
            const delay = networkRetryRef.current * 1200;
            console.log(`[Jarvis STT] Network hiccup — retry ${networkRetryRef.current}/3 in ${delay}ms...`);
            setPhase("idle");
            setTimeout(() => {
              if (phaseRef.current === "idle") doStart(true);
            }, delay);
          } else {
            networkRetryRef.current = 0;
            setPhase("idle");
            if (onError) onError(
              "Speech recognition can't reach Google's servers after 3 retries. " +
              "This is usually an ad blocker or firewall blocking speech.google.com — " +
              "whitelist it, or just type your question below."
            );
          }
          return;
        }

        setPhase("idle");
        if (e.error && onError) {
          const msgs = {
            "not-allowed":         "Microphone access is blocked. Click the lock icon in your address bar and allow mic.",
            "service-not-allowed": "Voice isn't available here — try Chrome or Edge.",
            "audio-capture":       "Can't reach your microphone. Is it connected?",
          };
          onError(msgs[e.error] || `Voice error: ${e.error}`);
        }
      };

      rec.onend = () => {
        console.log("[Jarvis STT] Ended. Final transcript:", finalTranscriptRef.current);
        listeningActiveRef.current = false;
        stopAudioMeter();
        if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);

        const final = finalTranscriptRef.current.trim();

        // Need at least 3 chars to avoid noise / single-sound false triggers
        if (phaseRef.current === "listening" && final.length >= 3) {
          playJarvisCue("stop");
          setIsInterim(false);
          sendMessage(final, language);
        } else if (phaseRef.current === "listening" && final.length > 0) {
          // Got something but too short — restart to wait for full sentence
          console.log("[Jarvis STT] Too short:", JSON.stringify(final), "— restarting...");
          setUserCaption("");
          setTimeout(() => doStart(true), 300);
        } else {
          setPhase("idle");
          setIsInterim(false);
        }
      };

      try { rec.start(); }
      catch (e) {
        console.error("[Jarvis STT] Failed to start:", e);
        listeningActiveRef.current = false;
        setPhase("idle");
      }
    }
  }, [supported, language, sendMessage, startAudioMeter, stopAudioMeter, onError]);

  // Stop everything
  const stopAll = useCallback(() => {
    try { recognitionRef.current?.abort(); } catch {}
    try { window.speechSynthesis?.cancel(); } catch {}
    try { 
      if (window.__currentAudio) {
        window.__currentAudio.pause();
        window.__currentAudio.currentTime = 0;
      }
    } catch {}
    stopAudioMeter();
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
    }
    setPhase("idle");
  }, [stopAudioMeter]);

  // Cleanup
  useEffect(() => () => stopAll(), [stopAll]);

  return {
    supported,
    phase,
    language,
    setLanguage,
    userCaption,
    aiCaption,
    isInterim,
    audioLevel,
    analyser: analyserRef.current,
    isIntroduced,
    sessionId,
    modelUsed,
    selectedVoiceName,   // e.g. "Microsoft Aria Online (Natural)"
    preferredVoiceName,
    setPreferredVoiceName,
    startListening,
    stopAll,
    introduce,
    voices,
    sendText: (t) => sendMessage(t, language),
  };
}
